import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Route } from '@/constants';
import { isBoolean } from '@/types';
import { useRegisterGAPageView } from '@/hooks';
import { projectItemListTransformer } from '@/features/projects/helpers';
import { useSendOpportunityStats } from '@/features/stats/hooks';
import { useBelongsToMyCompany } from '@/features/company/hooks';
import { serviceItemListTransformer } from '@/features/services/helpers';

import { useIsOpportunityEnabled, useOpportunityDetails } from '../../hooks';
import { OpportunityDetails } from './opportunity-details/opportunity-details';
import { OpportunityCollapsableProjects } from './opportunity-projects/opportunity-collapsable-projects';
import { useCurrentUser } from '@/features/users/hooks';
import { OpportunityServices } from './opportunity-details/opportunity-services';
import { Container } from '@/components';

export type OpportunityViewProps = {
  readonly id: string;
};

function OpportunityReadOnlyView({ projects, names, opportunityId, isPaid, belongsToMe, formattedServices }: any) {
  return (
    <>
      <OpportunityServices services={formattedServices} />
      <OpportunityDetails opportunityId={opportunityId} />
      <OpportunityCollapsableProjects
        projects={projects}
        names={names}
        opportunityId={opportunityId}
        isPaid={isPaid}
        belongsToMe={belongsToMe}
      />
    </>
  );
}

export function OpportunityView({ id }: OpportunityViewProps) {
  const navigate = useNavigate();
  const currentUser = useCurrentUser();

  const { normalizedOpportunity, opportunity } = useOpportunityDetails(id);
  const isOpportunityEnabled = useIsOpportunityEnabled(normalizedOpportunity);
  const opportunityBelongsToMyCompany = useBelongsToMyCompany(normalizedOpportunity?.company);

  const projects = opportunity?.projects.map((project) => project.project);
  const projectsName = opportunity?.projects.map((project) => project.name);

  const isOpportunityMine = normalizedOpportunity?.company === currentUser?.company?.id;
  const userHasOffered = opportunity?.offers?.some((offer) => offer.company?.id === currentUser?.company?.id);
  const canViewAllProjects = isOpportunityMine || userHasOffered;
  const filteredProjects = canViewAllProjects ? projects : projects?.filter((project) => project?.status !== 'closed');
  const projectItemList = filteredProjects?.map(projectItemListTransformer);
  const formattedServices = normalizedOpportunity?.advisors.map((advisor) => serviceItemListTransformer(advisor));

  const sendViewStats = useSendOpportunityStats({ type: 'view' });
  useRegisterGAPageView(normalizedOpportunity ? `Opportunity: ${normalizedOpportunity.name}` : null);

  useEffect(() => {
    if (!normalizedOpportunity) return;
    if (!opportunityBelongsToMyCompany) {
      sendViewStats(normalizedOpportunity.id);
    }
  }, [normalizedOpportunity?.id, opportunityBelongsToMyCompany, sendViewStats]);

  useEffect(() => {
    if (isBoolean(isOpportunityEnabled) && !isOpportunityEnabled) {
      navigate(Route.Home);
    }
  }, [isOpportunityEnabled, navigate]);

  if (!normalizedOpportunity || !projectItemList || !projects) return null;

  return (
    <div className="space-y-4 px-1 pt-4">
      <OpportunityReadOnlyView
        projects={projectItemList}
        names={projectsName}
        opportunityId={id}
        isPaid={normalizedOpportunity.isPaid}
        belongsToMe={opportunityBelongsToMyCompany}
        formattedServices={formattedServices}
      />
    </div>
  );
}
